import { cn } from "../lib/cn.js";

// Стандартные диапазоны качества (wear) в CS2.
// https://counterstrike.fandom.com/wiki/Skin_Float
export interface WearOption {
  key: string;
  label: string;
  short: string; // как отображается в названии скина: (FN), (MW)…
  min: number;
  max: number;
}

export const WEAR_OPTIONS: WearOption[] = [
  { key: "any", label: "Любое", short: "Any", min: 0, max: 1 },
  { key: "fn", label: "Factory New", short: "FN", min: 0.0, max: 0.07 },
  { key: "mw", label: "Minimal Wear", short: "MW", min: 0.07, max: 0.15 },
  { key: "ft", label: "Field-Tested", short: "FT", min: 0.15, max: 0.38 },
  { key: "ww", label: "Well-Worn", short: "WW", min: 0.38, max: 0.45 },
  { key: "bs", label: "Battle-Scarred", short: "BS", min: 0.45, max: 1.0 },
];

interface Props {
  // Реальный диапазон float'а конкретного скина из ByMykel.
  // Не все wear доступны для каждого скина: например AK Redline начинается с 0.1,
  // и FN/MW для него физически невозможны.
  skinMinFloat: number;
  skinMaxFloat: number;
  selectedKey: string;
  onChange: (option: WearOption) => void;
  disabled?: boolean;
}

// Пересечение [a, b] и [c, d]. Если пусто — wear для этого скина невозможен.
function intersects(aMin: number, aMax: number, bMin: number, bMax: number): boolean {
  return Math.max(aMin, bMin) < Math.min(aMax, bMax) - 0.0001;
}

export function WearPicker({
  skinMinFloat,
  skinMaxFloat,
  selectedKey,
  onChange,
  disabled,
}: Props) {
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {WEAR_OPTIONS.map((w) => {
        // "any" = весь диапазон скина — всегда доступен.
        const available =
          w.key === "any" || intersects(w.min, w.max, skinMinFloat, skinMaxFloat);
        const isSelected = w.key === selectedKey;
        return (
          <button
            key={w.key}
            type="button"
            disabled={disabled || !available}
            onClick={() => onChange(w)}
            className={cn(
              "rounded-lg px-3 py-2 text-left text-xs transition",
              isSelected
                ? "bg-button text-button-text"
                : available
                  ? "bg-bg-secondary hover:bg-bg"
                  : "bg-bg-secondary text-hint opacity-40",
            )}
            title={
              available
                ? `${w.min}–${w.max}`
                : `Этот скин не выпускается в качестве ${w.label}`
            }
          >
            <div className="font-medium">{w.label}</div>
            <div className={cn("text-[10px]", isSelected ? "opacity-90" : "text-hint")}>
              {w.min.toFixed(2)} – {w.max.toFixed(2)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Утилита: получить «эффективные» min/max для конкретного скина и выбранного wear.
// Это пересечение wear-диапазона и физического диапазона скина.
export function effectiveRange(
  skinMin: number,
  skinMax: number,
  wear: WearOption,
): { min: number; max: number } {
  const min = Math.max(skinMin, wear.min);
  const max = Math.min(skinMax, wear.max);
  return { min, max };
}
